/**
 * @file: useManualLeadForm.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * The staff-entered "create manual lead" form. The plate is typed or entered via
 * the shared license-plate keyboard, then the vehicle is looked up from existing
 * records to auto-fill its descriptor + VIN + mileage; the vehicle can also be
 * picked by brand → model. Customer, target service date, origin and assignee
 * are required; visibility / follow-up window / remarks round out the advanced
 * section. Submits a manual lead, then opens its detail page.
 *
 * The data fields live in a `useFormController`; transient UI state (the vehicle
 * lookup + picker visibilities) stays in plain state.
 */

import { AppNavParams } from '@euler/app/Routes';
import { useCurrentUser } from '@euler/app/flows/auth/context';
import { LeadsNavParams } from '@euler/app/flows/leads';
import { useDateTimePicker } from '@euler/components/DateTimePicker';
import { useAppLoading } from '@euler/components/loading/Provider';
import { valueAfterAutoFill } from '@euler/functions/valueAfterAutoFill';
import { useFormController } from '@euler/lib/form/controller';
import { VehicleInfoCore } from '@euler/model/VehicleInfoCore';
import { OrgMember } from '@euler/model/entity';
import {
  LeadOrigin,
  LeadOriginLabel,
  LeadType,
  LeadTypeLabel,
  LeadVisibility,
} from '@euler/model/lead/Lead';
import { CreateManualLeadRequest } from '@euler/model/lead/types';
import { VehicleModelInfo } from '@euler/model/vehicle';
import { useServiceFactory } from '@euler/services/factory';
import { useActionSheet } from '@expo/react-native-action-sheet';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import addDays from 'date-fns/addDays';
import format from 'date-fns/format';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Toast from 'react-native-root-toast';

// The follow-up window opens this many days ahead of the target service date.
const OUTREACH_LEAD_DAYS = 7;

// A vehicle descriptor entered through the brand → model picker (no VIN / fuel /
// year detail — those only arrive from an existing-record lookup).
type PickedVehicle = {
  vehicleName: string;
  vehicleBrandName: string;
  vehicleModel: string;
  vehicleManufacturer?: string;
};

// What the last plate lookup wrote into the form, so the next one can replace
// its own values without overwriting anything the staff typed.
type AutoFilledFields = {
  mileage: string;
  customerName: string;
  customerPhone: string;
};

type AssigneeRef = { id: number; name: string };

// Origin options offered in the action sheet — every origin except `system`,
// which is the value the auto-pipeline assigns and a human never picks.
const ORIGIN_OPTIONS = (Object.keys(LeadOriginLabel) as LeadOrigin[]).filter(
  origin => origin !== 'system',
);

// Lead-type options the staff pick from (the system-only `mixed` is excluded).
// Defaults to `acquisition` — a manually-created lead is a new customer.
const TYPE_OPTIONS: LeadType[] = ['acquisition', 'recurring', 'win_back'];

// The data fields backed by the form controller.
type ManualLeadFields = {
  licensePlateNo: string;
  vin: string;
  mileage: string;
  customerName: string;
  customerPhone: string;
  targetDate: Date;
  origin: LeadOrigin | undefined;
  type: LeadType;
  assignee: AssigneeRef | undefined;
  visibility: LeadVisibility;
  outreachStart: Date;
  remarks: string;
};

const toast = (message: string) =>
  Toast.show(message, { position: Toast.positions.CENTER });

export type ManualLeadFormState = ReturnType<typeof useManualLeadForm>;

export const useManualLeadForm = () => {
  const { leadService, vehicleInfoService } = useServiceFactory();
  const loading = useAppLoading();
  const { showActionSheetWithOptions } = useActionSheet();
  const navigation =
    useNavigation<StackNavigationProp<AppNavParams & LeadsNavParams>>();
  const currentUser = useCurrentUser();

  const defaultTarget = useMemo(
    () => addDays(new Date(), OUTREACH_LEAD_DAYS),
    [],
  );
  const defaultAssignee = useMemo<AssigneeRef | undefined>(
    () =>
      currentUser?.user.memberId
        ? { id: currentUser.user.memberId, name: currentUser.user.name }
        : undefined,
    [currentUser?.user.memberId, currentUser?.user.name],
  );

  const { form } = useFormController<ManualLeadFields>({
    licensePlateNo: { defaultValue: '' },
    vin: { defaultValue: '' },
    mileage: { defaultValue: '' },
    customerName: { defaultValue: '' },
    customerPhone: { defaultValue: '' },
    targetDate: { defaultValue: defaultTarget },
    origin: { defaultValue: undefined },
    type: { defaultValue: 'acquisition' },
    assignee: { defaultValue: defaultAssignee },
    visibility: { defaultValue: 'public' },
    outreachStart: {
      defaultValue: addDays(defaultTarget, -OUTREACH_LEAD_DAYS),
    },
    remarks: { defaultValue: '' },
  });

  // Transient UI state outside the form data.
  const [vehicle, setVehicle] = useState<VehicleInfoCore | undefined>(
    undefined,
  );
  const [pickedVehicle, setPickedVehicle] = useState<PickedVehicle | undefined>(
    undefined,
  );
  const [lookingUp, setLookingUp] = useState(false);
  const [outreachTouched, setOutreachTouched] = useState(false);

  const [targetPickerVisible, showTargetPicker, hideTargetPicker] =
    useDateTimePicker();
  const [outreachPickerVisible, showOutreachPicker, hideOutreachPicker] =
    useDateTimePicker();
  const [visibilityPickerVisible, setVisibilityPickerVisible] = useState(false);

  // Until the staff pick an outreach date, it tracks `target − lead days` so it
  // stays consistent with the backend default as the target changes (rather
  // than pinning to whatever "today − lead days" was at mount).
  const updateOutreach = form.outreachStart.update;
  const targetValue = form.targetDate.value;
  useEffect(() => {
    if (!outreachTouched) {
      updateOutreach(addDays(targetValue, -OUTREACH_LEAD_DAYS));
    }
  }, [targetValue, outreachTouched, updateOutreach]);
  const onPickOutreachStart = useCallback(
    (next: Date) => {
      updateOutreach(next);
      setOutreachTouched(true);
    },
    [updateOutreach],
  );

  const updatePlate = form.licensePlateNo.update;
  const updateVin = form.vin.update;

  // Typed plate entry: a new plate drops any resolved descriptor + VIN so a
  // stale one isn't kept (the plate keyboard applies the same reset on done).
  const onChangeLicensePlate = useCallback(
    (next: string) => {
      updatePlate(next);
      setVehicle(undefined);
      updateVin('');
    },
    [updatePlate, updateVin],
  );

  // The resolved vehicle label, preferring a manual pick over a lookup. Shows
  // "brand model" when both are known, else the bundled vehicle name.
  const vehicleLabel = useMemo(() => {
    const brandName =
      pickedVehicle?.vehicleBrandName ?? vehicle?.vehicleBrandName;
    const modelName = pickedVehicle?.vehicleModel ?? vehicle?.vehicleModel;
    const name = pickedVehicle?.vehicleName ?? vehicle?.vehicleName;
    if (brandName && modelName) return `${brandName} ${modelName}`;
    return name || '';
  }, [pickedVehicle, vehicle]);

  // Look the vehicle up from existing records so its descriptor, VIN, mileage
  // and the customer who last brought it in auto-fill; a hit clears any earlier
  // manual pick. A request-id guards against a slower earlier lookup landing
  // after a newer one.
  const mileageValue = form.mileage.value;
  const updateMileage = form.mileage.update;
  const customerNameValue = form.customerName.value;
  const updateCustomerName = form.customerName.update;
  const customerPhoneValue = form.customerPhone.value;
  const updateCustomerPhone = form.customerPhone.update;
  const lookupSeqRef = useRef(0);
  const autoFilledRef = useRef<AutoFilledFields | undefined>(undefined);
  const lookupVehicle = useCallback(
    async (plateArg: string) => {
      const plate = plateArg.trim();
      if (!plate) {
        setVehicle(undefined);
        return;
      }
      const seq = ++lookupSeqRef.current;
      setLookingUp(true);
      try {
        const found = await vehicleInfoService.getExistingVehicleInfo({
          licensePlateNo: plate,
        });
        if (seq !== lookupSeqRef.current) return;
        setVehicle(found ?? undefined);
        if (found) {
          setPickedVehicle(undefined);
          updateVin(found.vin ?? '');
        }
        // The customer is only carried for a vehicle this org has served, so a
        // miss (or another org's vehicle) leaves the fields to be entered by
        // hand rather than keeping the previous plate's customer.
        const filled: AutoFilledFields = {
          mileage: found?.vehicleMileage ? String(found.vehicleMileage) : '',
          customerName: found?.customerName ?? '',
          customerPhone: found?.customerMobile ?? '',
        };
        const prev = autoFilledRef.current;
        updateMileage(
          valueAfterAutoFill(mileageValue, filled.mileage, prev?.mileage),
        );
        updateCustomerName(
          valueAfterAutoFill(
            customerNameValue,
            filled.customerName,
            prev?.customerName,
          ),
        );
        updateCustomerPhone(
          valueAfterAutoFill(
            customerPhoneValue,
            filled.customerPhone,
            prev?.customerPhone,
          ),
        );
        autoFilledRef.current = filled;
      } catch (e) {
        console.error(e);
      } finally {
        if (seq === lookupSeqRef.current) setLookingUp(false);
      }
    },
    [
      customerNameValue,
      customerPhoneValue,
      mileageValue,
      updateCustomerName,
      updateCustomerPhone,
      updateMileage,
      updateVin,
      vehicleInfoService,
    ],
  );

  // Enter the plate via the shared license-plate keyboard (`_plate` overlay);
  // applying a new plate resets any resolved descriptor + VIN, then re-looks up.
  const plateValue = form.licensePlateNo.value;
  // Run the lookup against the currently-typed plate (the input's blur handler).
  const onLookupPlate = useCallback(() => {
    void lookupVehicle(plateValue);
  }, [lookupVehicle, plateValue]);

  const onOpenPlateKeyboard = useCallback(() => {
    navigation.navigate('_plate', {
      licensePlateNo: plateValue,
      onDone: (value: string) => {
        const plate = value.trim().toUpperCase();
        updatePlate(plate);
        setVehicle(undefined);
        updateVin('');
        void lookupVehicle(plate);
      },
    });
  }, [navigation, plateValue, updatePlate, updateVin, lookupVehicle]);

  const onPickVehicle = useCallback(() => {
    navigation.navigate('_vehicleBrandSelector', {
      selected: {
        brand: pickedVehicle?.vehicleBrandName ?? vehicle?.vehicleBrandName,
        model: pickedVehicle?.vehicleModel ?? vehicle?.vehicleModel,
      },
      onSelect: (brand: string, model: VehicleModelInfo) => {
        setPickedVehicle({
          vehicleName: `${brand} ${model.name}`,
          vehicleBrandName: brand,
          vehicleModel: model.name,
          vehicleManufacturer: model.manufacturer || undefined,
        });
        // A manual pick overrides the looked-up vehicle; it carries no VIN.
        setVehicle(undefined);
        updateVin('');
      },
    });
  }, [navigation, pickedVehicle, vehicle, updateVin]);

  const assigneeValue = form.assignee.value;
  const updateAssignee = form.assignee.update;
  const onPickAgent = useCallback(() => {
    navigation.push('_serviceAgentSelector', {
      selectedServiceAgentId: assigneeValue?.id,
      onSelect: (member: OrgMember) =>
        updateAssignee({ id: member.id, name: member.name }),
    });
  }, [navigation, assigneeValue?.id, updateAssignee]);

  const updateOrigin = form.origin.update;
  const onPickOrigin = useCallback(() => {
    const labels = ORIGIN_OPTIONS.map(o => LeadOriginLabel[o]);
    showActionSheetWithOptions(
      {
        options: [...labels, '取消'],
        cancelButtonIndex: labels.length,
        title: '选择线索来源',
      },
      index => {
        if (index != null && index < ORIGIN_OPTIONS.length) {
          updateOrigin(ORIGIN_OPTIONS[index]);
        }
      },
    );
  }, [showActionSheetWithOptions, updateOrigin]);

  const updateType = form.type.update;
  const onPickType = useCallback(() => {
    const labels = TYPE_OPTIONS.map(t => LeadTypeLabel[t]);
    showActionSheetWithOptions(
      {
        options: [...labels, '取消'],
        cancelButtonIndex: labels.length,
        title: '选择商机类型',
      },
      index => {
        if (index != null && index < TYPE_OPTIONS.length) {
          updateType(TYPE_OPTIONS[index]);
        }
      },
    );
  }, [showActionSheetWithOptions, updateType]);

  const openVisibilityPicker = useCallback(
    () => setVisibilityPickerVisible(true),
    [],
  );
  const closeVisibilityPicker = useCallback(
    () => setVisibilityPickerVisible(false),
    [],
  );
  const updateVisibility = form.visibility.update;
  const onSelectVisibility = useCallback(
    (next: LeadVisibility) => {
      updateVisibility(next);
      setVisibilityPickerVisible(false);
    },
    [updateVisibility],
  );

  const submit = useCallback(async () => {
    if (lookingUp) {
      toast('车辆识别中，请稍候');
      return;
    }
    const plate = form.licensePlateNo.value.trim();
    if (!plate) {
      toast('请输入车牌号');
      return;
    }
    const name = form.customerName.value.trim();
    if (!name) {
      toast('请输入客户姓名');
      return;
    }
    const phone = form.customerPhone.value.trim();
    if (!phone) {
      toast('请输入联系电话');
      return;
    }
    const origin = form.origin.value;
    if (!origin) {
      toast('请选择线索来源');
      return;
    }
    const assignee = form.assignee.value;
    if (!assignee) {
      toast('请选择负责人');
      return;
    }

    const mileageRaw = form.mileage.value.trim();
    const mileageNum = mileageRaw ? Number(mileageRaw) : null;
    const outreach = form.outreachStart.value;
    const req: CreateManualLeadRequest = {
      licensePlateNo: plate,
      vin: form.vin.value.trim() || null,
      currentMileage:
        mileageNum != null && !Number.isNaN(mileageNum) ? mileageNum : null,
      vehicleName: pickedVehicle?.vehicleName ?? vehicle?.vehicleName ?? null,
      vehicleBrandName:
        pickedVehicle?.vehicleBrandName ?? vehicle?.vehicleBrandName ?? null,
      vehicleManufacturer:
        pickedVehicle?.vehicleManufacturer ??
        vehicle?.vehicleManufacturer ??
        null,
      vehicleModel:
        pickedVehicle?.vehicleModel ?? vehicle?.vehicleModel ?? null,
      vehicleModelYear: vehicle?.vehicleModelYear ?? null,
      vehicleDisplacement: vehicle?.vehicleDisplacement ?? null,
      vehicleTrimLevel: vehicle?.vehicleTrimLevel ?? null,
      vehicleFuelType: vehicle?.vehicleFuelType ?? null,
      customerName: name,
      customerPhone: phone,
      assigneeId: assignee.id,
      assigneeName: assignee.name,
      targetServiceDate: format(form.targetDate.value, 'yyyy-MM-dd'),
      origin,
      type: form.type.value,
      visibility: form.visibility.value,
      outreachWindowStart: format(outreach, 'yyyy-MM-dd'),
      remarks: form.remarks.value.trim() || undefined,
    };

    await loading.showWithAction(async () => {
      const { leadId } = await leadService.createManualLead(req);
      navigation.replace('detail', { id: leadId });
    });
  }, [
    form,
    leadService,
    loading,
    lookingUp,
    navigation,
    pickedVehicle,
    vehicle,
  ]);

  return {
    // The form fields — read `form.<field>.value`, set `form.<field>.update`.
    form,
    // Behaviors that wrap a field update or sit outside the form data.
    onChangeLicensePlate,
    onLookupPlate,
    onOpenPlateKeyboard,
    vehicle,
    vehicleLabel,
    lookingUp,
    onPickVehicle,
    onPickOrigin,
    onPickType,
    onPickAgent,
    targetPickerVisible,
    showTargetPicker,
    hideTargetPicker,
    outreachPickerVisible,
    showOutreachPicker,
    hideOutreachPicker,
    onPickOutreachStart,
    visibilityPickerVisible,
    openVisibilityPicker,
    closeVisibilityPicker,
    onSelectVisibility,
    submit,
  };
};
